import * as THREE from 'three'


const loadImg = (url) => {
    return new Promise((resolve, reject) => {
        try {
            let img = new Image();
            img.crossOrigin = "Anonymous"; //解决跨域问题
            img.onload = function () {
                resolve(img);
            };
            img.src = url
        } catch (e) {
            reject(e);
            console.log(e);
        }
    });
}
// --- Three.js Helpers ---

// Generate a dense grid of points (0..1, 0..1) for the vertex shader to distort
function createGridMesh(subdivisions) {
    const vertices = [];
    const uvs = [];
    const indices = [];

    for (let y = 0; y <= subdivisions; y++) {
        for (let x = 0; x <= subdivisions; x++) {
            const u = x / subdivisions;
            const v = y / subdivisions;
            // Position will be modified by shader, start with UVs
            vertices.push(u - 0.5, v - 0.5, 0); // Center around origin
            uvs.push(u, 1 - v); // Flip V for Three.js texture coordinates
        }
    }

    for (let y = 0; y < subdivisions; y++) {
        for (let x = 0; x < subdivisions; x++) {
            const row1 = y * (subdivisions + 1);
            const row2 = (y + 1) * (subdivisions + 1);

            const a = row1 + x;
            const b = row1 + x + 1;
            const c = row2 + x;
            const d = row2 + x + 1;

            // Two triangles per square
            indices.push(a, b, c);
            indices.push(b, d, c);
        }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);

    return geometry;
}

// Helper to apply perspective transform (homography) to a point
const applyPerspectiveMatrix = (p, m) => {
    const x = p.x;
    const y = p.y;

    const numeratorX = m[0][0] * x + m[0][1] * y + m[0][2];
    const numeratorY = m[1][0] * x + m[1][1] * y + m[1][2];
    const w = m[2][0] * x + m[2][1] * y + m[2][2];

    const weight = w === 0 ? 0.00001 : w;

    return {
        x: numeratorX / weight,
        y: numeratorY / weight
    };
};

// Pure JavaScript Warp Renderer Class using Three.js
class SmartObjectRenderer {
    constructor({ size, bg_img }) {
        this.canvas = document.createElement('canvas');
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.mesh = null;
        this.texture = null;
        this.material = null;
        this.customMatrix = null;
        this.bg_img = null;
        this.tempCanvas = document.createElement('canvas');
        this.canvas.width = size[0];
        this.canvas.height = size[1];
        this.tempCanvas.width = size[0];
        this.tempCanvas.height = size[1]
        this.bg_img = bg_img;
        this.init()
    }


    // Initialize Three.js
    init() {

       try {
      
        // Create scene
        this.scene = new THREE.Scene();

        // Create camera (orthographic for 2D rendering)
        this.camera = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, -1, 1);

        // Create renderer
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            alpha: true,
            antialias: true,
            preserveDrawingBuffer: true // Important for toDataURL
        });

        // Update renderer size
        this.renderer.setSize(this.canvas.width, this.canvas.height);
        // Create geometry (64x64 grid)
        const geometry = createGridMesh(64);

        // Create material with custom shaders
        const material = new THREE.ShaderMaterial({
            uniforms: {
                uTexture: { value: null },
                uControlPoints: { value: new Array(16).fill(new THREE.Vector2()) },
                uProjectionMatrix: { value: new THREE.Matrix4() }
            },
            vertexShader: this.getVertexShader(),
            fragmentShader: this.getFragmentShader(),
            transparent: true,
            side: THREE.DoubleSide
        });

        this.material = material;
        this.mesh = new THREE.Mesh(geometry, material);
        this.scene.add(this.mesh);

        // Create initial texture
        this.texture = new THREE.Texture();
        this.texture.wrapS = THREE.ClampToEdgeWrapping;
        this.texture.wrapT = THREE.ClampToEdgeWrapping;
        this.texture.magFilter = THREE.LinearFilter;
        this.texture.minFilter = THREE.LinearFilter;

        return true;
       } catch (error) {
        console.error('Error initializing SmartObject:', error);
        return false;
       }
    }

    // Vertex shader for Bezier surface warping
    getVertexShader() {
        return `
        varying vec2 vUv;
        uniform vec2 uControlPoints[16];
        uniform mat4 uProjectionMatrix;

        // Bernstein polynomials
        float B0(float t) { return (1.0 - t) * (1.0 - t) * (1.0 - t); }
        float B1(float t) { return 3.0 * (1.0 - t) * (1.0 - t) * t; }
        float B2(float t) { return 3.0 * (1.0 - t) * t * t; }
        float B3(float t) { return t * t * t; }

        vec2 getBezierPoint(float u, float v) {
          vec2 p = vec2(0.0);
          
          // Basis functions for u and v
          float bu[4];
          bu[0] = B0(u); bu[1] = B1(u); bu[2] = B2(u); bu[3] = B3(u);
          
          float bv[4];
          bv[0] = B0(v); bv[1] = B1(v); bv[2] = B2(v); bv[3] = B3(v);

          int index = 0;
          for (int j = 0; j < 4; j++) {
            for (int i = 0; i < 4; i++) {
              p += uControlPoints[index] * bu[i] * bv[j];
              index++;
            }
          }
          return p;
        }

        void main() {
          vUv = uv;
          
          // Map uv (0..1) to the warped position using the control points.
          // Photoshop Mesh Points are ordered Top-Row to Bottom-Row.
          vec2 warpedPos = getBezierPoint(uv.x, 1.0 - uv.y);
          
          // Apply custom projection matrix to match original WebGL behavior
          gl_Position = uProjectionMatrix * vec4(warpedPos, 0.0, 1.0);
        }
        `;
    }

    // Fragment shader for texture rendering
    getFragmentShader() {
        return `
        precision highp float;
        varying vec2 vUv;
        uniform sampler2D uTexture;

        void main() {
          vec4 color = texture2D(uTexture, vUv);
          if (color.a < 0.01) discard;
          gl_FragColor = color;
        }
        `;
    }

    // Handle Image Loading
    async setTexture({ imageUrl, meshPoints, customMatrix }) {
        try {
            // this.init();
            let img = await loadImg(imageUrl);

            // Update texture
            this.texture.image = img;
            this.texture.needsUpdate = true;
            this.material.uniforms.uTexture.value = this.texture;

            this.drawScene(meshPoints, customMatrix);

            const texture = this.canvas.toDataURL('image/png');
            this.tempCanvas.width = this.canvas.width;
            this.tempCanvas.height = this.canvas.height;
            const ctx = this.tempCanvas.getContext('2d');
            const images = [this.bg_img, texture]
            for (let img of images) {
                let image = await loadImg(img)
                ctx.drawImage(image, 0, 0, this.tempCanvas.width, this.tempCanvas.height);
            }
            const dataURL = this.tempCanvas.toDataURL('image/png');
            // 释放 Three.js 资源
            this.cleanup();

            return dataURL;
        } catch (error) {
            console.log("🚀 ~ SmartObjectRenderer ~ loadImg ~ error:", error);
        }
    }

    // Main Draw Function
    drawScene(meshPoints, customMatrix) {
        // Check if meshPoints is empty or invalid
        if (!meshPoints || meshPoints.length === 0) {
            // Only apply matrix transformation without mesh warping
            this.drawSimpleTransform(customMatrix);
            return;
        }

        // 1. Process Control Points
        const rawPoints = meshPoints;
        if (rawPoints.length !== 16) return;

        // Apply Perspective Matrix & Coordinate System Transform
        // PS: Y is Down. Three.js: Y is Up. We flip Y by negating it.
        const points = rawPoints.map(p => {
            const t = applyPerspectiveMatrix(p, customMatrix);
            return { x: t.x, y: -t.y };
        });

        // 2. Calculate Non-Transparent Bounding Box
        const boundingBox = this.calculateNonTransparentBoundingBox(points);

        const contentWidth = boundingBox.maxX - boundingBox.minX;
        const contentHeight = boundingBox.maxY - boundingBox.minY;

        // Set display size to actual non-transparent area
        // this.canvas.width = Math.max(1, Math.ceil(contentWidth));
        // this.canvas.height = Math.max(1, Math.ceil(contentHeight));

        // Update renderer size
        // this.renderer.setSize(this.canvas.width, this.canvas.height);


        // Calculate projection matrix (same as original WebGL version)
        const w = contentWidth;
        const h = contentHeight;

        const projectionMatrix = new THREE.Matrix4();
        const elements = [
            2 / w, 0, 0, 0,
            0, 2 / h, 0, 0,
            0, 0, 1, 0,
            -(boundingBox.maxX + boundingBox.minX) / w, -(boundingBox.maxY + boundingBox.minY) / h, 0, 1
        ];
        projectionMatrix.fromArray(elements);

        // Update projection matrix uniform
        this.material.uniforms.uProjectionMatrix.value = projectionMatrix;

        // Update control points uniform
        const controlPoints = points.map(p => new THREE.Vector2(p.x, p.y));
        this.material.uniforms.uControlPoints.value = controlPoints;

        // Set camera to simple orthographic for 2D rendering
        this.camera.left = -0.5;
        this.camera.right = 0.5;
        this.camera.top = 0.5;
        this.camera.bottom = -0.5;
        this.camera.updateProjectionMatrix();

        // Reset mesh position (handled by projection matrix)
        this.mesh.position.set(0, 0, 0);

        // Render
        this.renderer.render(this.scene, this.camera);
    }

    // Calculate the bounding box of non-transparent area by sampling the Bezier surface
    calculateNonTransparentBoundingBox(points, sampleResolution = 32) {
        // Function to check if a point is within the Bezier surface bounds
        const isWithinSurface = (u, v) => {
            // Calculate Bezier surface point
            const point = this.getBezierSurfacePoint(u, v, points);

            // Check if this point contributes to the visible area
            // For a Bezier surface with 4x4 control points, the entire parameter space [0,1]x[0,1]
            // should map to a valid surface, but we need to exclude areas that map outside
            // the convex hull of the control points (which would indicate invalid mapping)

            return this.isValidBezierPoint(point, u, v, points);
        };

        // Sample the parameter space to find actual bounds
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        let hasValidPoints = false;

        for (let v = 0; v <= sampleResolution; v++) {
            for (let u = 0; u <= sampleResolution; u++) {
                const s = u / sampleResolution;
                const t = v / sampleResolution;

                if (isWithinSurface(s, t)) {
                    const point = this.getBezierSurfacePoint(s, t, points);

                    minX = Math.min(minX, point.x);
                    maxX = Math.max(maxX, point.x);
                    minY = Math.min(minY, point.y);
                    maxY = Math.max(maxY, point.y);
                    hasValidPoints = true;
                }
            }
        }

        // Fallback to simple bounding box if sampling fails
        if (!hasValidPoints) {
            points.forEach(p => {
                minX = Math.min(minX, p.x);
                maxX = Math.max(maxX, p.x);
                minY = Math.min(minY, p.y);
                maxY = Math.max(maxY, p.y);
            });
        }

        // Add small padding to avoid edge clipping
        const padding = 1;
        return {
            minX: minX - padding,
            maxX: maxX + padding,
            minY: minY - padding,
            maxY: maxY + padding
        };
    }

    // Calculate a single point on the Bezier surface
    getBezierSurfacePoint(u, v, points) {
        // Bernstein polynomials
        const B0 = (t) => (1 - t) * (1 - t) * (1 - t);
        const B1 = (t) => 3 * (1 - t) * (1 - t) * t;
        const B2 = (t) => 3 * (1 - t) * t * t;
        const B3 = (t) => t * t * t;

        const bu = [B0(u), B1(u), B2(u), B3(u)];
        const bv = [B0(v), B1(v), B2(v), B3(v)];

        let x = 0, y = 0;
        let index = 0;

        for (let j = 0; j < 4; j++) {
            for (let i = 0; i < 4; i++) {
                x += points[index].x * bu[i] * bv[j];
                y += points[index].y * bu[i] * bv[j];
                index++;
            }
        }

        return { x, y };
    }

    // Check if a Bezier surface point is valid (within reasonable bounds)
    isValidBezierPoint(point, _u, _v, controlPoints) {
        // Calculate the convex hull bounds of control points
        let minCX = Infinity, maxCX = -Infinity;
        let minCY = Infinity, maxCY = -Infinity;

        controlPoints.forEach(p => {
            minCX = Math.min(minCX, p.x);
            maxCX = Math.max(maxCX, p.x);
            minCY = Math.min(minCY, p.y);
            maxCY = Math.max(maxCY, p.y);
        });

        // Check if the point is within a reasonable range of control points
        // Add tolerance to account for Bezier surface extension beyond control points
        const tolerance = Math.max(maxCX - minCX, maxCY - minCY) * 0.5;

        return point.x >= (minCX - tolerance) &&
            point.x <= (maxCX + tolerance) &&
            point.y >= (minCY - tolerance) &&
            point.y <= (maxCY + tolerance) &&
            !isNaN(point.x) &&
            !isNaN(point.y) &&
            isFinite(point.x) &&
            isFinite(point.y);
    }

    // Draw with simple matrix transformation only (no mesh warping)
    drawSimpleTransform(customMatrix) {
        // Get the image dimensions
        const img = this.texture.image;
        if (!img) return;

        const imgWidth = img.width;
        const imgHeight = img.height;

        // Define the four corners of the image in pixel coordinates
        const corners = [
            { x: 0, y: 0 },                 // top-left
            { x: imgWidth, y: 0 },           // top-right  
            { x: imgWidth, y: imgHeight },   // bottom-right
            { x: 0, y: imgHeight }           // bottom-left
        ];

        // Apply perspective matrix to each corner
        const transformedCorners = corners.map(p => {
            const t = applyPerspectiveMatrix(p, customMatrix);
            return { x: t.x, y: -t.y }; // Flip Y for Three.js coordinate system
        });

        // Calculate bounding box of transformed image
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        transformedCorners.forEach(p => {
            minX = Math.min(minX, p.x);
            maxX = Math.max(maxX, p.x);
            minY = Math.min(minY, p.y);
            maxY = Math.max(maxY, p.y);
        });

        // Calculate projection matrix that maps UV coordinates to the transformed corners
        const projectionMatrix = new THREE.Matrix4();

        // Use a simpler approach: apply the transformation directly to control points

        // Calculate projection matrix to properly display the transformed mesh
        const contentWidth = maxX - minX;
        const contentHeight = maxY - minY;

        const elements = [
            2 / contentWidth, 0, 0, 0,
            0, 2 / contentHeight, 0, 0,
            0, 0, 1, 0,
            -(maxX + minX) / contentWidth, -(maxY + minY) / contentHeight, 0, 1
        ];
        projectionMatrix.fromArray(elements);

        // Apply the matrix transform to control points to simulate the transformation
        const transformedControlPoints = [];
        for (let row = 0; row < 4; row++) {
            for (let col = 0; col < 4; col++) {
                const u = col / 3; // 0, 0.333, 0.667, 1
                const v = row / 3; // 0, 0.333, 0.667, 1

                // Convert normalized to pixel coordinates
                const pixelX = u * imgWidth;
                const pixelY = v * imgHeight;

                // Apply perspective matrix
                const transformed = applyPerspectiveMatrix({ x: pixelX, y: pixelY }, customMatrix);

                // Flip Y for Three.js and store
                transformedControlPoints.push(new THREE.Vector2(transformed.x, -transformed.y));
            }
        }

        // Update uniforms
        this.material.uniforms.uProjectionMatrix.value = projectionMatrix;
        this.material.uniforms.uControlPoints.value = transformedControlPoints;

        // Set camera to simple orthographic for 2D rendering
        this.camera.left = -0.5;
        this.camera.right = 0.5;
        this.camera.top = 0.5;
        this.camera.bottom = -0.5;
        this.camera.updateProjectionMatrix();

        // Reset mesh position
        this.mesh.position.set(0, 0, 0);

        // Render
        this.renderer.render(this.scene, this.camera);
    }



    // Cleanup
    cleanup() {
        // Dispose geometry
        if (this.mesh && this.mesh.geometry) {
            this.mesh.geometry.dispose();
        }

        // Dispose material and shader program
        if (this.material) {
            this.material.dispose();
        }

        // Dispose texture
        if (this.texture) {
            this.texture.dispose();
        }

        // Dispose renderer and WebGL context
        if (this.renderer) {
            this.renderer.dispose();
            this.renderer = null;
        }

        // Clear scene references
        if (this.scene) {
            this.scene.clear();
            this.scene = null;
        }

        // Release canvas elements
        if (this.canvas) {
            // Force release WebGL context
            const gl = this.canvas.getContext('webgl') || this.canvas.getContext('experimental-webgl');
            if (gl && gl.getExtension('WEBGL_lose_context')) {
                gl.getExtension('WEBGL_lose_context').loseContext();
            }
            this.canvas.width = 1;
            this.canvas.height = 1;
            this.canvas = null;
        }

        if (this.tempCanvas) {
            this.tempCanvas.width = 1;
            this.tempCanvas.height = 1;
            this.tempCanvas = null;
        }

        this.mesh = null;
        this.camera = null;
        this.bg_img = null;
        this.customMatrix = null;

    }
}

export default SmartObjectRenderer;
